import moment from 'moment'
import { Operation, Service } from '@/libraries/permissions'
import { store } from '@/store/store'

export function parseCameraConfig (apiResponse) {
  const baseCameraState = {
    cameraDetectablesById: {},
    camerasById: {},
  }
  return apiResponse.reduce((config, camera) => {
    config.camerasById[camera.uuid] = {
      uuid: camera.uuid,
      description: camera.description,
      id: camera.id,
      tags: camera.tags,
      fname: camera.fname,
      logging: camera.detection_logging_enabled,
      detectables: [],
    }
    if (camera.alert_config) {
      const alertIds = camera.alert_config.map(alert => alert.alert_config_id)
      config.camerasById[camera.uuid].alertsById = alertIds
    }
    if (camera.detectable_config) {
      const currDetectablesById = camera.detectable_config.reduce(function (obj, detConf) {
        const conf = {
          id: detConf.detectable_id,
          detectable: detConf.detectable.name,
          color: detConf.detectable.color,
          enabled: detConf.detection_enabled,
          confidence: detConf.minimum_confidence,
          retention: detConf.retention,
        }
        obj[detConf.detectable_config_id] = conf
        return obj
      }, {})
      config.camerasById[camera.uuid].detectables = camera.detectable_config.map(detConf => detConf.detectable_config_id)
      config.cameraDetectablesById = { ...config.cameraDetectablesById, ...currDetectablesById }
    }
    return config
  }, baseCameraState)
}

export function parseAlertConfig (apiResponse) {
  const baseAlertState = {
    alertsById: {},
    camerasById: {},
    notificationsById: {},
    conditionsById: {},
  }
  return apiResponse.reduce((config, alertConfig) => {
    const alertId = alertConfig.alert_config_id
    const currentAlert = { description: alertConfig.description, cameras: [], notifications: [], conditions: [], rate_limit: alertConfig.schedule.rate_limit }
    alertConfig.cameras.forEach(camera => {
      config.camerasById[camera.uuid] = { uuid: camera.uuid, description: camera.description }
      currentAlert.cameras.push(camera.uuid)
    })
    if (alertConfig.notifications) {
      alertConfig.notifications.forEach(notification => {
        config.notificationsById[notification.notification_config_id] = {
          activeDays: activeDayValuesToString(notification.active_days.split('')),
          endOffset: notification.end_offset,
          endOffsetFrom: notification.end_offset_from,
          startOffset: notification.start_offset,
          startOffsetFrom: notification.start_offset_from,
          schedule_id: notification.notification_schedule_id,
          info: notification.info,
          type: notification.type,
          target_id: notification.notification_target_id,
        }
        currentAlert.notifications.push(notification.notification_config_id)
      })
    }
    if (alertConfig.object_detection_conditions) {
      alertConfig.object_detection_conditions.forEach(condition => {
        config.conditionsById[condition.condition_config_id] = {
          id: condition.condition_config_id,
          debounce: condition.debounce,
          detectableDescription: condition.detectable.name,
          detectableId: condition.detectable.id,
          expiration: condition.expires_after,
          min: condition.minimum_count,
          max: condition.maximum_count,
          confidence: condition.minimum_confidence,
          duration: condition.minimum_duration,
          maxDuration: condition.maximum_duration,
        }
        currentAlert.conditions.push(condition.condition_config_id)
      })
    }
    config.alertsById[alertId] = currentAlert
    return config
  }, baseAlertState)
}

export function detectionFieldsFromConfig (detectionConfigs, detectables) {
  return Object.entries(detectionConfigs).map(config => {
    let opts
    const detectionConf = config[1]
    const configId = config[0]
    if (detectables) {
      opts = detectables.map(detectable => detectable.class_name)
    } else opts = ['waiting to download detectables', detectionConf.detectable.name]

    const confidence = {
      label: 'Confidence',
      value: detectionConf.confidence,
      type: 'number',
      formatter: (value) => parseFloat(value),
      validator: (value) => {
        const float = parseFloat(value)
        return float <= 1 && float >= 0.0
      },
      identifiers: { detectable: configId, key: 'confidence' },
    }

    const detectable = {
      label: 'Detectable',
      value: detectionConf.detectable,
      type: 'select',
      options: opts,
      identifiers: { detectable: configId, key: 'detectable' },
    }

    const enabled = {
      label: 'Enabled',
      value: detectionConf.enabled,
      type: 'checkbox',
      identifiers: { detectable: configId, key: 'enabled' },
    }

    const retention = {
      label: 'Retention',
      value: secondsToDuration(detectionConf.retention),
      validator: validateDurationString,
      formatter: parseDurationString,
      type: 'text',
      identifiers: { detectable: configId, key: 'retention' },
    }
    // console.log(confidence, detectable, enabled, retention)
    return {
      title: 'Camera Detectable',
      identifiers: { detectable: configId, key: 'detectables' },
      initialValues: detectionConf,
      fields: [confidence, detectable, enabled, retention],
    }
  })
}

export function cameraFieldsFromConfig (camera) {
  const description = {
    label: 'Description',
    value: camera.description,
    type: 'text',
    readOnly: true,
    identifiers: { camera: camera.uuid, key: 'description' },
  }
  const logging = {
    label: 'Logging Enabled',
    value: camera.logging,
    type: 'checkbox',
    identifiers: { camera: camera.uuid, key: 'logging' },
  }
  return {
    title: 'Camera Info',
    identifiers: { camera: camera.uuid, key: 'cameras' },
    initialValues: camera,
    fields: [description, logging],
  }
}

export function groupsFromConditionConfig (conditions, detectables) {
  return Object.entries(conditions).map(function (configPair) {
    const conditionId = configPair[0]
    const condition = configPair[1]
    const confidence = {
      label: 'Minimum Confidence',
      value: condition.confidence,
      type: 'number',
      formatter: (value) => parseFloat(value),
      validator: (value) => {
        const float = parseFloat(value)
        return float <= 1 && float >= 0.1
      },
      identifiers: { condition: conditionId, key: 'confidence' },
    }
    const max = {
      label: 'Maximum Count',
      value: condition.max,
      type: 'number',
      min: 1,
      max: 2147483647,
      identifiers: { condition: conditionId, key: 'max' },
    }
    const min = {
      label: 'Minimum Count',
      value: condition.min,
      type: 'number',
      min: 1,
      max: 2147483647,
      identifiers: { condition: conditionId, key: 'min' },
    }
    const debounce = {
      label: 'Debounce',
      value: secondsToDuration(condition.debounce),
      validator: validateDurationString,
      formatter: parseDurationString,
      type: 'text',
      identifiers: { condition: conditionId, key: 'debounce' },
    }
    const maximumDuration = {
      label: 'Maximum Duration',
      value: secondsToDuration(condition.maxDuration),
      validator: validateDurationString,
      formatter: parseDurationString,
      optional: true,
      type: 'text',
      identifiers: { condition: conditionId, key: 'maxDuration' },
    }
    const minimumDuration = {
      label: 'Minimum Duration',
      value: secondsToDuration(condition.duration),
      validator: validateDurationString,
      formatter: parseDurationString,
      type: 'text',
      identifiers: { condition: conditionId, key: 'duration' },
    }
    const expiration = {
      label: 'Expiration',
      value: secondsToDuration(condition.expiration),
      validator: validateDurationString,
      formatter: parseDurationString,
      type: 'text',
      identifiers: { condition: conditionId, key: 'expiration' },
    }
    let opts
    if (detectables) {
      opts = detectables.map(detectable => detectable.class_name)
    } else opts = ['waiting to download detectables', condition.detectableDescription]
    const detectable = {
      label: 'Detectable',
      value: condition.detectableDescription,
      type: 'select',
      identifiers: { condition: conditionId, key: 'detectableDescription' },
      options: opts,
    }
    const conditionGroup = {
      title: 'Object Detection Conditions',
      identifiers: { condition: conditionId, key: 'conditions' },
      initialValues: condition,
      fields: [detectable, confidence, min, max, debounce, maximumDuration, minimumDuration, expiration],
    }
    return conditionGroup
  })
}

export function groupsFromNotificationConfig (notifications) {
  const offsetFromOptions = ['sunrise', 'sunset', 'midnight']
  const mappedGroups = Object.entries(notifications).map(configPair => {
    const notificationId = configPair[0]
    const notification = configPair[1]
    const notificationGroup = {
      title: 'Notification',
      identifiers: { notification: notificationId, key: 'notifications' },
      initialValues: notification,
      fields: [],
    }

    notificationGroup.fields.push({
      label: 'Active Days',
      type: 'select',
      multiple: true,
      value: notification.activeDays,
      options: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      identifiers: { notification: notificationId, key: 'activeDays' },
    })
    notificationGroup.fields.push({
      label: 'Start Offset From',
      value: notification.startOffsetFrom,
      type: 'select',
      options: offsetFromOptions,
      identifiers: { notification: notificationId, key: 'startOffsetFrom' },
    })
    notificationGroup.fields.push({
      label: 'Start Offset',
      value: secondsToDuration(notification.startOffset),
      validator: validateDurationString,
      formatter: parseDurationString,
      type: 'text',
      identifiers: { notification: notificationId, key: 'startOffset' },
    })
    notificationGroup.fields.push({
      label: 'End Offset From',
      value: notification.endOffsetFrom,
      type: 'select',
      options: offsetFromOptions,
      identifiers: { notification: notificationId, key: 'endOffsetFrom' },
    })
    notificationGroup.fields.push({
      label: 'End Offset',
      value: secondsToDuration(notification.endOffset),
      validator: validateDurationString,
      formatter: parseDurationString,
      type: 'text',
      identifiers: { notification: notificationId, key: 'endOffset' },
    })
    const info = typeof notification.info === 'object' ? Object.entries(notification.info)[0][1] : notification.info
    const hasLimitedEditing = store.getters['entitlements/canPerformOperationForService'](Service.ALERTING, Operation.LIMITED_EDIT)
    const hasFullEditing = store.getters['entitlements/canPerformOperationForService'](Service.ALERTING, Operation.EDIT)
    notificationGroup.fields.push({
      readOnly: hasLimitedEditing && !hasFullEditing,
      label: 'Target',
      value: info,
      type: 'text',
      identifiers: { notification: notificationId, key: 'info' },
    })
    notificationGroup.fields.push({
      readOnly: hasLimitedEditing && !hasFullEditing,
      label: 'Medium',
      value: notification.type,
      type: 'select',
      identifiers: { notification: notificationId, key: 'type' },
      options: ['slack', 'email', 'teams'],
    })

    return notificationGroup
  })
  return mappedGroups
}

export function daysToIntString (daysArr) {
  const daysArray = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const out = daysArray.reduce((prev, curr) => {
    if (daysArr.includes(curr)) {
      prev += '1'
    } else prev += '0'
    return prev
  }, '')
  return out
}

function activeDayValuesToString (values) {
  const daysArray = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const mapped = [...values].map((val, index) => {
    if (val === '1') {
      return daysArray[index]
    }
  })
    .filter(day => !!day)
  return mapped
}

function validateDurationString (input) {
  const validUnits = ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds', 'year', 'month', 'week', 'day', 'hour', 'minute', 'second']
  const inputsArray = input.split(' ').filter(chunk => chunk.length > 0)

  // loop excludes trailing values (e.g. '1 days 3' or '5 days seconds')
  for (let idx = 0; idx < inputsArray.length;) {
    if (Number.isInteger(parseInt(inputsArray[idx])) && validUnits.includes(inputsArray[idx + 1])) {
      idx += 2
    } else return false
  }
  return true
}

export function parseDurationString (input) {
  const inputsArray = input.split(' ').filter(chunk => chunk.length > 0)
  const duration = {}
  for (let idx = 0; idx < inputsArray.length;) {
    duration[inputsArray[idx + 1]] = inputsArray[idx]
    idx += 2
  }
  return moment.duration(duration).asSeconds()
}

function secondsToDuration (offset) {
  if (offset == null) {
    return undefined
  }
  const interval = moment.duration({ seconds: offset })
  return offset < 60 ? `${offset} seconds` : interval.humanize()
}
